import React, { useState, ChangeEvent, FC, SVGProps, Fragment } from 'react';
// components
import { FormattedMessage } from 'react-intl';
import FetchOnceMultiSelect from 'components/Select/FetchOnceMultiSelect';
import FilterSectionWrapper from '../FilterSectionWrapper';
import CheckboxMenu from '../Menu/CheckboxMenu';
import SelectedItem from '../Menu/SelectedItem';
// api
import { getGenetics } from 'requests/genetics';
// types
import { ConditionOptionItem, Genetic } from 'types';
import { GeneticsSectionType, SectionExtraStateProps } from 'types/filterSections';
import { ValueType } from 'react-select';
import { OptionKeysTypes } from 'components/Select/Select.types';
// utils
import isEqual from 'lodash.isequal';

type GeneticsSectionProps = {
  conditions: ConditionOptionItem[];
  isDisabled: boolean;
  isMaleGenetics?: boolean;
  labelKeys: { [key in GeneticsSectionType['condition']]?: string };
  onOptionSelect: (
    value: GeneticsSectionType['condition'],
    options?: GeneticsSectionType['options'],
    others?: { genetics?: Genetic[] }
  ) => void;
  onRemove: () => void;
  section: SectionExtraStateProps & GeneticsSectionType;
  triggerIcon: FC<SVGProps<SVGSVGElement>>;
};

const GeneticsSection = ({
  conditions,
  isDisabled,
  isMaleGenetics,
  labelKeys,
  onOptionSelect,
  onRemove,
  section,
  triggerIcon,
}: GeneticsSectionProps) => {
  const { condition: savedCondition, genetics, isInvalid, isInitialOpened, options } = section;
  const selectedIds = options?.[0].value || [];
  const [state, setState] = useState({
    condition: savedCondition,
    selected: genetics || [],
    selectedIds,
  });
  const { condition, selected } = state;

  const handleConditionChange = ({ target: { name } }: ChangeEvent<HTMLInputElement>) => {
    setState({
      condition: name as GeneticsSectionType['condition'],
      selected: [],
      selectedIds: [],
    });
  };

  const handleSelect = (option?: ValueType<OptionKeysTypes & { details: Genetic }>) => {
    if (option) {
      // todo: check react-select new version to fix it
      const selectedGenetic = (option as OptionKeysTypes & { details: Genetic }).details;
      setState(prevState => ({ ...prevState, selected: [...prevState.selected, selectedGenetic] }));
    }
  };

  const handleRemove = (option: OptionKeysTypes & { details: Genetic }) => {
    if (option) {
      setState(prevState => ({
        ...prevState,
        selected: prevState.selected.filter(item => item.id !== option.value),
      }));
    }
  };

  const handleApply = () => {
    onOptionSelect(
      condition,
      [{ key: 'genetic_ids', value: selected?.map(item => item.id) || [] }],
      { genetics: selected }
    );
  };

  const resetData = () => {
    const newState: typeof state = {
      selected: genetics || [],
      condition: savedCondition,
      selectedIds,
    };
    const isEqualState = isEqual(newState, state);
    if (!isEqualState) setState(newState);
  };

  const triggerLabel =
    isInvalid || !savedCondition ? (
      <FormattedMessage id="filters.emptyGenetics" />
    ) : (
      <FormattedMessage id={labelKeys?.[savedCondition]} values={{ count: selectedIds?.length }} />
    );

  const optionFormatter = (item: Genetic) => ({
    value: item.id,
    label: `${item.name} (${item.provider})`,
    details: item,
  });

  return (
    <FilterSectionWrapper
      currentValue={condition}
      isInitialOpened={isInitialOpened}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      isRemovable
      onRemove={onRemove}
      onClose={resetData}
      triggerIcon={triggerIcon}
      triggerLabel={triggerLabel}
    >
      <CheckboxMenu
        options={conditions}
        onConditionChange={handleConditionChange}
        onApply={handleApply}
        currentValue={condition}
      >
        {value => (
          <Fragment>
            {value === condition && (
              <div className="p-10">
                <FetchOnceMultiSelect
                  listType={isMaleGenetics ? 'male-genetics' : 'genetics'}
                  isSmall
                  selected={selected}
                  onSelect={handleSelect}
                  optionFormatter={optionFormatter}
                  fetchData={isMaleGenetics ? () => getGenetics({ type: 'male' }) : getGenetics}
                  placeholder={<FormattedMessage id="general.placeholder.searchGenetics" />}
                >
                  {values => (
                    <div className="mt-10">
                      {values.map(item => (
                        <SelectedItem
                          key={item.value}
                          label={item.label}
                          onRemove={() => handleRemove(item)}
                        />
                      ))}
                    </div>
                  )}
                </FetchOnceMultiSelect>
              </div>
            )}
          </Fragment>
        )}
      </CheckboxMenu>
    </FilterSectionWrapper>
  );
};

export default GeneticsSection;
